/**
 * The settled day-ahead OOTD review contract.
 *
 * Queue rows come from:
 *
 *   GET /api/v1/stylist/ootd/day-ahead/queue
 *       ?review_status=awaiting_review&limit=50&offset=0
 *
 * `review_status=` (empty) returns every status and `member_id` narrows the
 * read to one member, which is how a member's past daily looks are listed.
 *
 * One slot, whatever its review status, comes from:
 *
 *   GET /api/v1/stylist/ootd/day-ahead/{slot_id}
 *
 * Decisions are keyed by the queue row's day-ahead slot id, not by its
 * optional styling-outfit reference:
 *
 *   POST /api/v1/stylist/ootd/day-ahead/{slot_id}/decision
 *   { "final_garment_ids": [...], "comment": null | "..." }
 *
 * The server derives approved vs edited and the garment diff. An unchanged
 * approval sends a null comment; a changed set must carry a non-empty comment.
 *
 * Every member has one assigned stylist, who reviews first; anyone can help.
 * The queue takes `scope=mine|others|all` (default all, mine first) and each
 * row carries `is_mine` and the assigned stylist. A decision on someone
 * else's member comes back with `covered_for_stylist_id`. An older API sends
 * none of this, so every ownership field here is optional and a queue without
 * it is read, and requested, exactly as before.
 *
 * Every member gets three looks a day, one queue row each (`look_index` 1-3),
 * ordered by member then look. A stylist can reject a look:
 *
 *   POST /api/v1/stylist/ootd/day-ahead/{slot_id}/reject
 *   { "comment": null | "..." }
 *
 * The rejected look stays as `rejected` and a replacement with the same
 * `look_index` is generated (`awaiting_generation`, `replaces_slot_id` set),
 * then joins the review queue. Repeating a reject returns the same replacement
 * with `idempotent: true`. A look the generator gave up on is `unfilled`.
 * An older API sends no look fields, so they are optional too.
 */

import {
  dayAheadDecisionApiV1StylistOotdDayAheadSlotIdDecisionPost,
  dayAheadQueueApiV1StylistOotdDayAheadQueueGet,
  dayAheadRejectApiV1StylistOotdDayAheadSlotIdRejectPost,
  dayAheadSlotApiV1StylistOotdDayAheadSlotIdGet,
} from '@closet-theory/api';
import { configureStylistApi, handleUnauthorized } from './auth';
import { targetDateFor } from './review-cutoff';

export type ReviewStatus =
  | 'awaiting_generation'
  | 'awaiting_review'
  | 'approved'
  | 'shipped_unactioned'
  | 'rejected'
  | 'unfilled';

export type RenderStatus = 'not_started' | 'rendering' | 'ready' | 'failed';

export type ReviewGarment = {
  garmentId: string;
  title: string;
  category: string;
  tags: string[];
  imageUrl: string | null;
};

/** The only member details supplied by the day-ahead queue. */
export type MemberContext = {
  displayName: string;
  avatarUrl: string | null;
  /** The member's full email; absent from an older API. */
  email?: string | null;
};

export type QueuedOutfit = {
  /** `ootd_day_ahead_slots.id`, used for routing and decisions. */
  slotId: string;
  memberId: string;
  forDate: string;
  location: string | null;
  /** Reference only; decisions must never be keyed with this value. */
  stylingOutfitId: string | null;
  reviewStatus: ReviewStatus;
  renderStatus: RenderStatus;
  renderError: string | null;
  createdAt: string;
  originalGarmentIds: string[];
  garments: ReviewGarment[];
  context: MemberContext;
  /** The stylist id that approved or edited this slot; null until someone does. */
  reviewedBy?: string | null;
  /** The reviewing stylist's name; null until someone reviews, or when the API omits it. */
  reviewedByName?: string | null;
  reviewedAt?: string | null;
  /** The reviewer's note on an edit; null for a clean approval. */
  stylistComment?: string | null;
  /** Why the generator put these garments together; null when it gave no reason. */
  compatibilityReason?: string | null;
  /**
   * Who reviews this member first. Absent when the API does not report
   * ownership yet - that means "not reported", never "not mine".
   */
  ownership?: Ownership;
  /** Which of the member's daily looks this is (1-based). Absent from an older API. */
  lookIndex?: number;
  /** The rejected look this one replaces. */
  replacesSlotId?: string;
  /** Why the generator could not fill this look (`unfilled` only). */
  fillError?: string;
};

export type Ownership = {
  /** The member is assigned to the signed-in stylist. */
  isMine: boolean;
  assignedStylistId: string | null;
  assignedStylistName: string | null;
};

/** Which members' outfits a queue read returns. */
export type QueueScope = 'mine' | 'others' | 'all';

export type ReviewQueue = {
  /** The day shown by the current page (or tomorrow while an empty page loads). */
  targetDate: string;
  outfits: QueuedOutfit[];
  total: number;
  limit: number;
  offset: number;
};

export type ReviewQueueRequest = {
  /** Defaults to `awaiting_review`; an empty string asks for every status (history). */
  reviewStatus?: ReviewStatus | '';
  limit?: number;
  offset?: number;
  /** Server-side filter on member name or email. */
  q?: string;
  /** Sent only when set; an API without scoping ignores nothing it never received. */
  scope?: QueueScope;
  /** Only this member's slots. */
  memberId?: string;
};

/** One stylist decision, keyed by the day-ahead slot. */
export type ReviewSubmission = {
  slotId: string;
  finalGarmentIds: string[];
  comment: string;
};

export type DecisionReceipt = {
  decisionId?: string;
  decision: 'approved' | 'edited';
  garmentsAdded: string[];
  garmentsRemoved: string[];
  /** Set when the decider was not the member's assigned stylist. */
  coveredForStylistId?: string;
};

/** Rejecting one look, keyed by its day-ahead slot; the comment is optional. */
export type LookRejection = {
  slotId: string;
  comment: string;
};

export type RejectReceipt = {
  decisionId?: string;
  lookIndex?: number;
  /** The look generated in place of the rejected one. */
  replacementSlotId: string | null;
  /** The reject had already been recorded; this is the same replacement. */
  idempotent: boolean;
  coveredForStylistId?: string;
};

export type ReviewService = {
  loadQueue(request?: ReviewQueueRequest): Promise<ReviewQueue>;
  /** One slot by id, in any review status; undefined when it does not exist. */
  loadSlot(slotId: string): Promise<QueuedOutfit | undefined>;
  submitDecision(submission: ReviewSubmission): Promise<DecisionReceipt | undefined>;
  rejectLook(rejection: LookRejection): Promise<RejectReceipt | undefined>;
};

export function submissionBlockedReason(
  submission: ReviewSubmission,
  originalGarmentIds: readonly string[],
): string | undefined {
  if (!diffCombination(originalGarmentIds, submission.finalGarmentIds).changed) return undefined;
  if (!submission.comment.trim()) {
    return 'An edit needs a comment explaining what you changed and why.';
  }
  if (submission.finalGarmentIds.length === 0) {
    return 'An edited outfit needs at least one garment.';
  }
  return undefined;
}

/** Presentation only; the server independently derives and stores this diff. */
export function diffCombination(
  originalGarmentIds: readonly string[],
  finalGarmentIds: readonly string[],
): { addedGarmentIds: string[]; removedGarmentIds: string[]; changed: boolean } {
  const original = new Set(originalGarmentIds);
  const final = new Set(finalGarmentIds);
  const addedGarmentIds = finalGarmentIds.filter((id) => !original.has(id));
  const removedGarmentIds = originalGarmentIds.filter((id) => !final.has(id));
  return {
    addedGarmentIds,
    removedGarmentIds,
    changed: addedGarmentIds.length > 0 || removedGarmentIds.length > 0,
  };
}

type WireGarment = {
  garment_id?: unknown;
  title?: unknown;
  category?: unknown;
  tags?: unknown;
  image_url?: unknown;
};

type WireMember = {
  display_name?: unknown;
  avatar_url?: unknown;
  email?: unknown;
  email_masked?: unknown;
};

type WireOutfit = {
  id?: unknown;
  member_id?: unknown;
  for_date?: unknown;
  location?: unknown;
  styling_outfit_id?: unknown;
  garment_ids?: unknown;
  review_status?: unknown;
  render_status?: unknown;
  render_error?: unknown;
  created_at?: unknown;
  reviewed_by?: unknown;
  reviewed_by_name?: unknown;
  reviewed_at?: unknown;
  stylist_comment?: unknown;
  compatibility_reason?: unknown;
  is_mine?: unknown;
  assigned_stylist_id?: unknown;
  assigned_stylist_name?: unknown;
  look_index?: unknown;
  replaces_slot_id?: unknown;
  fill_error?: unknown;
  member?: WireMember;
  garments?: WireGarment[];
};

type WireQueue = {
  items?: unknown;
  total?: unknown;
  limit?: unknown;
  offset?: unknown;
};

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function nullableText(value: unknown): string | null {
  return text(value) || null;
}

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

function integer(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : fallback;
}

function wireOutfits(value: unknown): WireOutfit[] {
  return Array.isArray(value)
    ? value.filter(
        (item): item is WireOutfit =>
          item !== null && typeof item === 'object' && !Array.isArray(item),
      )
    : [];
}

function garment(wire: WireGarment): ReviewGarment {
  return {
    garmentId: text(wire.garment_id),
    title: text(wire.title) || 'Untitled garment',
    category: text(wire.category),
    tags: strings(wire.tags),
    imageUrl: nullableText(wire.image_url),
  };
}

function reviewStatus(value: unknown): ReviewStatus {
  if (
    value === 'awaiting_generation' ||
    value === 'approved' ||
    value === 'shipped_unactioned' ||
    value === 'rejected' ||
    value === 'unfilled'
  ) {
    return value;
  }
  return 'awaiting_review';
}

function renderStatus(value: unknown): RenderStatus {
  if (value === 'rendering' || value === 'ready' || value === 'failed') return value;
  return 'not_started';
}

function memberContext(wire: WireMember | undefined, memberId: string): MemberContext {
  // `email` is the full address; an API from before it existed sends only `email_masked`.
  const email = nullableText(wire?.email) ?? nullableText(wire?.email_masked);
  return {
    displayName: text(wire?.display_name) || memberId,
    avatarUrl: nullableText(wire?.avatar_url),
    ...(email ? { email } : {}),
  };
}

export function toDecisionReceipt(payload: unknown): DecisionReceipt | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const wire = payload as {
    decision_id?: unknown;
    decision?: unknown;
    garments_added?: unknown;
    garments_removed?: unknown;
    covered_for_stylist_id?: unknown;
  };
  if (wire.decision !== 'approved' && wire.decision !== 'edited') return undefined;
  return {
    ...(text(wire.decision_id) ? { decisionId: text(wire.decision_id) } : {}),
    decision: wire.decision,
    garmentsAdded: strings(wire.garments_added),
    garmentsRemoved: strings(wire.garments_removed),
    ...(text(wire.covered_for_stylist_id)
      ? { coveredForStylistId: text(wire.covered_for_stylist_id) }
      : {}),
  };
}

export function toRejectReceipt(payload: unknown): RejectReceipt | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const wire = payload as {
    decision_id?: unknown;
    decision?: unknown;
    look_index?: unknown;
    replacement_slot_id?: unknown;
    idempotent?: unknown;
    covered_for_stylist_id?: unknown;
  };
  if (wire.decision !== 'rejected') return undefined;
  const lookIndex = integer(wire.look_index, 0);
  return {
    ...(text(wire.decision_id) ? { decisionId: text(wire.decision_id) } : {}),
    ...(lookIndex ? { lookIndex } : {}),
    replacementSlotId: nullableText(wire.replacement_slot_id),
    idempotent: wire.idempotent === true,
    ...(text(wire.covered_for_stylist_id)
      ? { coveredForStylistId: text(wire.covered_for_stylist_id) }
      : {}),
  };
}

function ownership(wire: WireOutfit): Ownership | undefined {
  if (typeof wire.is_mine !== 'boolean') return undefined;
  return {
    isMine: wire.is_mine,
    assignedStylistId: nullableText(wire.assigned_stylist_id),
    assignedStylistName: nullableText(wire.assigned_stylist_name),
  };
}

function toQueuedOutfit(wire: WireOutfit): QueuedOutfit {
  const garments = (wire.garments ?? []).map(garment);
  const originalGarmentIds = Array.isArray(wire.garment_ids)
    ? strings(wire.garment_ids)
    : garments.map((item) => item.garmentId);
  const memberId = text(wire.member_id);
  const owner = ownership(wire);
  const lookIndex = integer(wire.look_index, 0);
  const replacesSlotId = text(wire.replaces_slot_id);
  const fillError = text(wire.fill_error);
  return {
    slotId: text(wire.id),
    memberId,
    forDate: text(wire.for_date),
    location: nullableText(wire.location),
    stylingOutfitId: nullableText(wire.styling_outfit_id),
    reviewStatus: reviewStatus(wire.review_status),
    renderStatus: renderStatus(wire.render_status),
    renderError: nullableText(wire.render_error),
    createdAt: text(wire.created_at),
    originalGarmentIds,
    garments,
    context: memberContext(wire.member, memberId),
    reviewedBy: nullableText(wire.reviewed_by),
    reviewedByName: nullableText(wire.reviewed_by_name),
    reviewedAt: nullableText(wire.reviewed_at),
    stylistComment: nullableText(wire.stylist_comment),
    compatibilityReason: nullableText(wire.compatibility_reason),
    ...(owner ? { ownership: owner } : {}),
    ...(lookIndex ? { lookIndex } : {}),
    ...(replacesSlotId ? { replacesSlotId } : {}),
    ...(fillError ? { fillError } : {}),
  };
}

const REVIEW_STATUS_LABELS: Record<ReviewStatus, string> = {
  awaiting_generation: 'Still generating',
  awaiting_review: 'Awaiting review',
  approved: 'Approved',
  shipped_unactioned: 'Shipped as generated',
  rejected: 'Rejected',
  unfilled: "Couldn't create this look",
};

/** A slot's review status in a stylist's words. */
export function reviewStatusLabel(status: ReviewStatus): string {
  return REVIEW_STATUS_LABELS[status];
}

/**
 * Who reviewed a slot: "You" when the signed-in stylist did, otherwise the
 * reviewer's name from the queue. When the API sends no name, any reviewer
 * other than the assigned stylist is named by their relation to them.
 * Undefined while nobody has reviewed it.
 */
export function reviewerLabel(outfit: QueuedOutfit): string | undefined {
  if (!outfit.reviewedBy) return undefined;
  const owner = outfit.ownership;
  const byAssigned = !!owner?.assignedStylistId && outfit.reviewedBy === owner.assignedStylistId;
  if (owner?.isMine && byAssigned) return 'You';
  if (outfit.reviewedByName) return outfit.reviewedByName;
  if (!owner) return 'A stylist';
  if (byAssigned) return owner.assignedStylistName ?? 'The assigned stylist';
  if (owner.isMine) return 'Another stylist, covering for you';
  return owner.assignedStylistName
    ? `A stylist covering for ${owner.assignedStylistName}`
    : 'A stylist';
}

/** Whether the API behind this page reports each member's numbered daily looks. */
export function reportsLooks(outfits: readonly QueuedOutfit[]): boolean {
  return outfits.some((outfit) => outfit.lookIndex !== undefined);
}

/**
 * A member's looks together, in look order: members keep the order they first
 * appear in, and a member's looks follow by `lookIndex`.
 */
export function byMemberThenLook(outfits: readonly QueuedOutfit[]): QueuedOutfit[] {
  const firstSeen = new Map<string, number>();
  outfits.forEach((outfit, index) => {
    if (!firstSeen.has(outfit.memberId)) firstSeen.set(outfit.memberId, index);
  });
  return outfits
    .map((outfit, index) => ({ outfit, index }))
    .sort(
      (a, b) =>
        firstSeen.get(a.outfit.memberId)! - firstSeen.get(b.outfit.memberId)! ||
        (a.outfit.lookIndex ?? 0) - (b.outfit.lookIndex ?? 0) ||
        a.index - b.index,
    )
    .map(({ outfit }) => outfit);
}

/** Whether the API behind this page reports who each member is assigned to. */
export function reportsOwnership(outfits: readonly QueuedOutfit[]): boolean {
  return outfits.some((outfit) => outfit.ownership !== undefined);
}

type Result = {
  data?: unknown;
  error?: unknown;
  response?: { status: number } | Response | undefined;
};

const ALREADY_REVIEWED =
  'This slot was already reviewed. Refresh the queue to see its latest state.';

function settle(
  result: Result,
  onUnauthorized: (status?: number) => void,
  failure: string,
  conflict = ALREADY_REVIEWED,
) {
  const status = result.response?.status;
  onUnauthorized(status);
  const failed =
    (result.error !== undefined && result.error !== null) ||
    (status !== undefined && status >= 400);
  if (failed) {
    if (status === 409) {
      throw new Error(conflict);
    }
    throw new Error(`${failure}${status ? ` · HTTP ${status}` : ''}`);
  }
  return result.data;
}

const DEFAULT_LIMIT = 50;

export function createReviewService({
  configure = configureStylistApi,
  onUnauthorized = handleUnauthorized,
  now = () => new Date(),
}: {
  configure?: () => void;
  onUnauthorized?: (status?: number) => void;
  now?: () => Date;
} = {}): ReviewService {
  return {
    async loadQueue(request = {}) {
      const limit = request.limit ?? DEFAULT_LIMIT;
      const offset = request.offset ?? 0;
      const q = request.q?.trim();
      configure();
      const payload = settle(
        await dayAheadQueueApiV1StylistOotdDayAheadQueueGet({
          query: {
            review_status: request.reviewStatus ?? 'awaiting_review',
            ...(q ? { q } : {}),
            ...(request.scope ? { scope: request.scope } : {}),
            ...(request.memberId ? { member_id: request.memberId } : {}),
            limit,
            offset,
          },
        }),
        onUnauthorized,
        "Today's review queue could not be loaded.",
      );
      const bareItems = Array.isArray(payload) ? payload : undefined;
      const envelope =
        !bareItems && payload !== null && typeof payload === 'object'
          ? (payload as WireQueue)
          : undefined;
      const outfits = wireOutfits(bareItems ?? envelope?.items).map(toQueuedOutfit);
      return {
        targetDate: outfits[0]?.forDate || targetDateFor(now()),
        outfits,
        total: bareItems ? bareItems.length : integer(envelope?.total, outfits.length),
        limit: bareItems ? limit : integer(envelope?.limit, limit),
        offset: bareItems ? offset : integer(envelope?.offset, offset),
      };
    },
    async loadSlot(slotId) {
      configure();
      const result = await dayAheadSlotApiV1StylistOotdDayAheadSlotIdGet({
        path: { slot_id: slotId },
      });
      if (result.response?.status === 404) return undefined;
      const payload = settle(result, onUnauthorized, 'This outfit could not be loaded.');
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return undefined;
      return toQueuedOutfit(payload);
    },
    async submitDecision(submission) {
      configure();
      const payload = settle(
        await dayAheadDecisionApiV1StylistOotdDayAheadSlotIdDecisionPost({
          path: { slot_id: submission.slotId },
          body: {
            final_garment_ids: submission.finalGarmentIds,
            comment: submission.comment.trim() || null,
          },
        }),
        onUnauthorized,
        'This review could not be recorded.',
      );
      return toDecisionReceipt(payload);
    },
    async rejectLook(rejection) {
      configure();
      const payload = settle(
        await dayAheadRejectApiV1StylistOotdDayAheadSlotIdRejectPost({
          path: { slot_id: rejection.slotId },
          body: { comment: rejection.comment.trim() || null },
        }),
        onUnauthorized,
        'This look could not be rejected.',
        'This look is no longer waiting for review. Refresh the queue to see its latest state.',
      );
      return toRejectReceipt(payload);
    },
  };
}

export const reviewService = createReviewService();
